import React, { useEffect, useState } from "react";

// COMPONENTS
import Box from "@/main/components/shared/box";
import Button from "@/main/components/shared/button/button";
import KogAutoCompleteControlled from "@/main/components/shared/inputAutoComplete/autoCompleteControlled";
import { AreaButton } from "@/main/components/shared/styles";

// REDUX/UTILS/HOOKS
import useFormQueryParams from "@/main/hooks/useformQueryParams";
import { KeyValue } from "@/main/models/common";
import {
	getDefaultCountryOptionDrop,
	getDefaultOptionDrop,
	getTranslateByKey,
	isNullUndefined,
	removeEmptyValues,
} from "@/main/services/utils";
import { getCountriesUser, useCountryReducer } from "@/main/store/country";
import { getInsurers, resetReducerInsurers, useInsurerReducer } from "@/main/store/insurer";
import { useDispatch } from "react-redux";
import KogInputTextControlledNew from "@/main/components/shared/inputText/inputTextControlledNew";
import { userIsAdmin, userIsInsurer } from "@/main/services/utilsUser";
import { InsurerFilterRequest, ValuatorFilterRequest } from "@/main/models/request";
import { getValuators, resetReducerValuators, useValuatorReducer } from "@/main/store/valuator";
import { getStatusRegister, useStatusRegisterReducer } from "@/main/store/statusRegister";
import SecondaryButton from "@/main/components/shared/button/secondaryButton";
import { useNavigate } from "react-router-dom";
import { RoutesConstants } from "@/main/routes/routesConstants";
import { HeadQuarterBranchFilterEnum } from "@/main/services/enum/HeadQuarterBranchFilterEnum";
import { StatusRegisterEnum } from "@/main/services/enum/StatusRegisterEnum";

import * as S from "./styles";

interface IFilterSearchValuatorProps {
	showFilters: boolean;
	setShowFilters: React.Dispatch<React.SetStateAction<boolean>>;
	onResetSearch: () => void;
	getDataSearchValuator: (filters: ValuatorFilterRequest) => void;
}

function FiltersSearchValuator({
	showFilters,
	setShowFilters,
	onResetSearch,
	getDataSearchValuator,
}: IFilterSearchValuatorProps) {
	//#region Hooks
	const dispatch = useDispatch();
	//#endregion

	//#region Reducers
	const { countriesUser: countryReducer } = useCountryReducer();
	const { insurers: insurerReducer } = useInsurerReducer();
	const { valuators: valuatorReducer } = useValuatorReducer();
	const { statusRegister: statusRegisterReducer } = useStatusRegisterReducer();
	//#endregion

	//#region States
	const optionDefaultCountryDrop = getDefaultCountryOptionDrop(countryReducer.data);
	const optionDefaultValuatorDrop = getDefaultOptionDrop(valuatorReducer.data);
	const optionDefaultInsurerDrop = userIsInsurer() ? getDefaultOptionDrop(insurerReducer.data) : null;

	const INITIAL_FORM_STATE = {
		country: null as KeyValue | null,
		valuator: null as KeyValue | null,
		rfc: "",
		statusRegister: null as KeyValue | null,
		insurer: null as KeyValue | null,
		name: "",
	};
	type ISearchValuatorForm = typeof INITIAL_FORM_STATE;

	const methods = useFormQueryParams<ISearchValuatorForm>({
		mode: "onChange",
		defaultValues: INITIAL_FORM_STATE,
		mapDefaultValues: {
			country: countryReducer,
			insurer: insurerReducer,
			valuator: valuatorReducer,
			statusRegister: statusRegisterReducer,
		},
		onSubmit: onSubmit,
	});
	const {
		control,
		setValue,
		formState: { errors },
		clearErrors,
		reset,
		customHandleSubmit,
	} = methods;
	//#endregion

	//#region Core functions
	function handleClear() {
		onResetSearch();
		reset(INITIAL_FORM_STATE);
		setValue("country", optionDefaultCountryDrop);
		if (statusRegisterReducer.data !== null) {
			const keyStatusRegister = String(StatusRegisterEnum.Active);
			const valueStatusRegister = String(statusRegisterReducer.data?.find((x) => x.key === keyStatusRegister)?.value);
			setValue("statusRegister", {
				key: keyStatusRegister,
				value: valueStatusRegister,
			});
		}
		setValue("name", "");

		clearErrors();

		if (optionDefaultCountryDrop !== null) {
			callInsurerApiDomain();
			callValuatorApiDomain();
		} else {
			dispatch(resetReducerInsurers());
			dispatch(resetReducerValuators());
		}
	}

	function onSubmit(formInputs: ISearchValuatorForm) {
		const request: ValuatorFilterRequest = {
			guidCountry: String(formInputs.country?.key),
			guidInsurer: String(formInputs.insurer?.key),
			guidValuator: String(formInputs.valuator?.key),
			idStatusRegister: formInputs.statusRegister?.key ? Number(formInputs.statusRegister.key) : null,
			registrationNumber: formInputs.rfc,
			name: formInputs.name,
		};

		getDataSearchValuator(removeEmptyValues(request));
		setShowFilters(false);
	}
	//#endregion

	// #region Async functions
	function callInsurerApiDomain() {
		dispatch(resetReducerInsurers());

		if (optionDefaultCountryDrop !== null) {
			const request: InsurerFilterRequest = {
				guidCountry: String(optionDefaultCountryDrop?.key),
				type: HeadQuarterBranchFilterEnum.All,
				idStatusRegister: StatusRegisterEnum.Active,
			};

			dispatch(getInsurers(request));
		}
	}

	function callValuatorApiDomain() {
		dispatch(resetReducerValuators());

		if (optionDefaultCountryDrop != null) {
			const request: ValuatorFilterRequest = {
				guidCountry: String(optionDefaultCountryDrop?.key),
				idStatusRegister: StatusRegisterEnum.Active,
			};

			dispatch(getValuators(request));
		}
	}
	//#endregion

	//#region UseEffects
	useEffect(() => {
		if (isNullUndefined(countryReducer.data)) dispatch(getCountriesUser());
		if (isNullUndefined(statusRegisterReducer.data)) dispatch(getStatusRegister());
		return () => {
			dispatch(resetReducerInsurers());
			dispatch(resetReducerValuators());
		};
	}, []);

	useEffect(() => {
		if (!isNullUndefined(optionDefaultCountryDrop)) {
			callInsurerApiDomain();
			callValuatorApiDomain();
		}
	}, [optionDefaultCountryDrop]);

	useEffect(() => {
		const valuatorValue = methods.watch("valuator");
		if (valuatorValue?.value) {
			setValue("name", valuatorValue.value);
		} else {
			setValue("name", "");
		}
	}, [methods.watch("valuator")]);
	//#endregion

	//#region Render constants
	const isDisabledInsurersDrop = () => userIsInsurer();
	const ID_SCREEN = "id-searchValuator";
	const generateIdScreen = (id: string) => `${ID_SCREEN}-${id}`;
	const hasLoadingReducers = insurerReducer.isLoading || countryReducer.isLoading || valuatorReducer.isLoading;

	const isDisabledValuatorDrop = valuatorReducer.data === null || valuatorReducer.data.length === 1;
	//#endregion

	return (
		<Box showBox={showFilters} title={getTranslateByKey("information.filters")}>
			<form onSubmit={customHandleSubmit} noValidate>
				<S.RowInputs>
					<KogAutoCompleteControlled
						required
						id={generateIdScreen("select-country")}
						nameCustom="country"
						control={control}
						label={getTranslateByKey("information.country")}
						options={countryReducer.data}
						isLoading={countryReducer.isLoading}
						rulesCustom={{ required: true }}
						errorMessageCustom={
							errors.country?.type === "required" ? getTranslateByKey("information.countryRequired", true) : ""
						}
						hasErrorCustom={!!errors.country}
						onChangeCustom={() => {
							callInsurerApiDomain();
							callValuatorApiDomain();
						}}
						valueDefaultKey={optionDefaultCountryDrop?.key}
						setValueCustom={setValue}
						disabled={optionDefaultCountryDrop !== null}
					/>
					<KogAutoCompleteControlled
						id={generateIdScreen("select-valuator")}
						nameCustom="valuator"
						isDefaultValueUnique={optionDefaultValuatorDrop !== null}
						setValueCustom={setValue}
						label={getTranslateByKey("information.name")}
						control={control}
						rulesCustom={{ required: false }}
						errorMessageCustom=""
						hasErrorCustom={false}
						options={valuatorReducer.data}
						isLoading={valuatorReducer.isLoading}
						disabled={isDisabledValuatorDrop}
						onChangeCustom={() => {}}
					/>
					<KogInputTextControlledNew
						id={generateIdScreen("txt-rfc")}
						label={getTranslateByKey("information.rfc")}
						helperText={
							errors.rfc?.type === "minLength" ? getTranslateByKey("information.rfcMustHaveAtLeast12Digits", true) : ""
						}
						name="rfc"
						error={!!errors.rfc}
						inputProps={{ maxLength: 20 }}
						controllerProps={{ rules: { minLength: 12 } }}
						control={control}
					/>
				</S.RowInputs>

				<S.RowInputs>
					<KogAutoCompleteControlled
						id={generateIdScreen("select-status")}
						label={getTranslateByKey("information.status")}
						nameCustom="statusRegister"
						valueDefaultKey={String(StatusRegisterEnum.Active)}
						errorMessageCustom=""
						control={control}
						setValueCustom={setValue}
						options={statusRegisterReducer.data}
						isLoading={statusRegisterReducer.isLoading}
						rulesCustom={{ required: false }}
						hasErrorCustom={false}
						onChangeCustom={() => {}}
					/>
					<KogAutoCompleteControlled
						id={generateIdScreen("select-insurer")}
						nameCustom="insurer"
						isDefaultValueUnique={optionDefaultInsurerDrop !== null}
						setValueCustom={setValue}
						label={getTranslateByKey("information.insurer")}
						control={control}
						rulesCustom={{ required: !userIsAdmin() }}
						errorMessageCustom={
							errors.insurer?.type === "required" ? getTranslateByKey("information.insurerRequired", true) : ""
						}
						hasErrorCustom={!!errors.insurer}
						options={insurerReducer.data}
						isLoading={insurerReducer.isLoading}
						disabled={isDisabledInsurersDrop()}
						onChangeCustom={() => {}}
					/>
				</S.RowInputs>

				<S.AreaButtons>
					<SecondaryButton id={generateIdScreen("btn-submit")} disabled={hasLoadingReducers} type="submit">
						{getTranslateByKey("information.search")}
					</SecondaryButton>
					<SecondaryButton id={generateIdScreen("btn-clear")} type="button" onClick={handleClear}>
						{getTranslateByKey("information.clear")}
					</SecondaryButton>
				</S.AreaButtons>
			</form>
		</Box>
	);
}

function FiltersSearchValuatorWrapper(props: Omit<IFilterSearchValuatorProps, "showFilters" | "setShowFilters">) {
	//#region States
	const [showFilters, setShowFilters] = useState<boolean>(true);
	//#endregion

	//#region hooks
	const navigate = useNavigate();
	//#endregion

	//#region HandleFunctions
	function handleFilters() {
		setShowFilters((prev) => !prev);
		window.scrollTo({
			top: 0,
			behavior: "smooth",
		});
	}

	function handleNew() {
		navigate(RoutesConstants.registrationRegisterValuator);
	}
	//#endregion

	const mergedProps: IFilterSearchValuatorProps = {
		...props,
		showFilters,
		setShowFilters,
	};

	const canCreateNew = userIsAdmin() || userIsInsurer();
	return (
		<>
			<AreaButton>
				<Button onClick={handleFilters} id="id-searchValuator-btn-filters">
					{getTranslateByKey("information.filters")}
				</Button>
				{canCreateNew && (
					<Button onClick={handleNew} id="id-searchValuator-btn-newValuator">
						{getTranslateByKey("information.newValuator")}
					</Button>
				)}
			</AreaButton>
			<FiltersSearchValuator {...mergedProps} />
		</>
	);
}

export default FiltersSearchValuatorWrapper;
